import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  Typography,
  UploadAudienceIcon,
} from '@acceso/design-system';
import { useTranslation } from 'react-i18next';

import { AudienceChannelSourceType } from '@/features/broadcasters/types';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { routerManager } from '@/routes/app-routes';
import { AVRouteEnum } from '@/routes/constants/AVRouteEnum';

import { useDeleteAudienceChannelSource } from '../api/delete-audience-channel-source';
import { AudienceChannelSourcesTable } from '../components/AudienceChannelSourcesTable/AudienceChannelSourcesTable';
import { useAudiencePermissions } from '../hooks/useAudiencePermissions';

export default function AudienceChannelSourcesPage() {
  const { t } = useTranslation();
  const { canDelete, canCreate } = useAudiencePermissions();

  const deleteDialog = useDisclosureWithData<AudienceChannelSourceType>();
  const { mutate: deleteChannelSource, isPending: isDeleting } = useDeleteAudienceChannelSource();

  const onDelete = (item: AudienceChannelSourceType) => {
    deleteDialog.open(item);
  };

  const handleConfirmDelete = () => {
    if (!deleteDialog.data) return;
    deleteChannelSource(
      { id: deleteDialog.data.id },
      {
        onSuccess: () => {
          deleteDialog.close();
        },
      }
    );
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between py-size-8">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Fuentes de audiencia')}
          </Typography>

          {canCreate && (
            <IconButtonSection
              onClick={() => routerManager.to(AVRouteEnum.AUDIENCES_LIST)}
              internalProps={{
                icon: {
                  size: IconSizeEnum.SM,
                  type: UploadAudienceIcon,
                },
                text: {
                  value: t('Cargar fichero de audiencias'),
                  color: FontColorEnum.Default,
                },
              }}
            />
          )}
        </div>

        <AudienceChannelSourcesTable onDelete={canDelete ? onDelete : undefined} />
      </div>

      <ModalDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.close}
        onAction={handleConfirmDelete}
        heading={t('Eliminar fuente de audiencia')}
        actionLabel={isDeleting ? t('Eliminando…') : t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage
          title={t('¿Estás seguro de que quieres eliminar esta fuente de audiencia?')}
          description={t(
            'Esta acción marcará el registro como eliminado. Los datos de audiencia relacionados no se verán afectados.'
          )}
        />
      </ModalDialog>
    </>
  );
}
